import { useEffect, useState, useCallback } from "react";
import { api, formatApiError, fileUrl, formatDate } from "@/lib/api";
import { Moon, Utensils, Apple, Droplets, NotebookPen, Camera, Trash2, X, Plus, Search, Package } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const PASTO_OPTS = [["niente", "Niente"], ["poco", "Poco"], ["abbondante", "Abbondante"], ["tutto", "Tutto"], ["bis", "Bis"], ["meta", "Metà (storico)"]];
const EMPTY_ENTRY = { pisolino_dalle: "", pisolino_alle: "", pisolino_note: "", pisolini: [], pranzo: "", merenda: "", spuntino_pietanza: "", spuntino_quantita: "", pranzo_pietanza: "", pranzo_quantita: "", pranzo_primo_pietanza: "", pranzo_primo_quantita: "", pranzo_secondo_pietanza: "", pranzo_secondo_quantita: "", pranzo_contorno_pietanza: "", pranzo_contorno_quantita: "", merenda_pietanza: "", merenda_quantita: "", bisogni: "", note: "" };
const EMPTY_MENU = { spuntino: [], pranzo_primo: [], pranzo_secondo: [], pranzo_contorno: [], merenda: [] };
const MEAL_FIELDS = [["spuntino","Spuntino",Apple],["pranzo_primo","Pranzo · Primo",Utensils],["pranzo_secondo","Pranzo · Secondo",Utensils],["pranzo_contorno","Pranzo · Contorno",Utensils],["merenda","Merenda",Apple]];
const currentTime24 = () => {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
};
const formatClockDigits = (value) => {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 4);
  return digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;
};
const clockError = (value, afterValue = "") => {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits.length) return "";
  if (digits.length !== 4) return "Inserisci quattro cifre nel formato HH:MM.";
  if (Number(digits.slice(0, 2)) > 24) return "Le ore devono essere comprese tra 00 e 24.";
  if (Number(digits.slice(2, 4)) > 59) return "I minuti devono essere compresi tra 00 e 59.";
  const afterDigits = String(afterValue || "").replace(/\D/g, "");
  if (afterDigits.length === 4) {
    const selectedMinutes = Number(digits.slice(0, 2)) * 60 + Number(digits.slice(2, 4));
    const afterMinutes = Number(afterDigits.slice(0, 2)) * 60 + Number(afterDigits.slice(2, 4));
    if (selectedMinutes <= afterMinutes) return `L’orario “Alle” deve essere successivo a “Dalle” (${afterValue}).`;
  }
  return "";
};

function NumericTimeInput({ value, onChange, label, afterValue = "" }) {
  const [error, setError] = useState("");
  const [originalValue, setOriginalValue] = useState(value || "");
  const updateValue = (event) => {
    const next = formatClockDigits(event.target.value);
    const message = clockError(next, afterValue);
    event.target.setCustomValidity(message);
    setError(String(next).replace(/\D/g, "").length === 4 ? message : "");
    onChange(next);
  };
  const finishEditing = (event) => {
    const message = event.target.value === originalValue ? "" : clockError(event.target.value, afterValue);
    event.target.setCustomValidity(message);
    setError(message);
  };
  return <div><input
    type="text"
    inputMode="numeric"
    enterKeyHint="done"
    autoComplete="off"
    maxLength={5}
    value={value || ""}
    aria-label={label}
    placeholder="HH:MM"
    onFocus={(event) => { setOriginalValue(event.currentTarget.value); event.currentTarget.select(); }}
    onChange={updateValue}
    onBlur={finishEditing}
    className={`input-warm text-center font-semibold tabular-nums tracking-wider ${error ? "!border-red-500 !ring-red-200" : ""}`}
  />{error && <p className="mt-1 text-xs font-semibold text-red-600">{error}</p>}</div>;
}
const unifiedMenu = (data) => {
  if (data.menu_unico && Object.values(data.menu_unico).some((values) => values?.length)) return { ...EMPTY_MENU, ...data.menu_unico };
  const legacy = { spuntino:data.menu_spuntino||[], pranzo_primo:data.menu_pranzo||[], pranzo_secondo:[], pranzo_contorno:[], merenda:data.menu_merenda||[] };
  return Object.fromEntries(Object.keys(EMPTY_MENU).map((meal) => [meal, [...new Set([
    ...(legacy[meal] || []), ...Object.values(data.menu_sezioni || {}).flatMap((menu) => menu?.[meal] || []),
  ].filter(Boolean))]]));
};

export default function Diario() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(new Date().toISOString().slice(0, 10));
  const [sezione, setSezione] = useState("");
  const [rows, setRows] = useState([]);
  const [foto, setFoto] = useState([]);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState(EMPTY_ENTRY);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [didascalia, setDidascalia] = useState("");
  const [bimboIdsSel, setBimboIdsSel] = useState([]);
  const [fotoSezione, setFotoSezione] = useState("");
  const [fotoBimbi, setFotoBimbi] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sezioni, setSezioni] = useState([]);
  const [menu, setMenu] = useState(EMPTY_MENU);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkStep, setBulkStep] = useState(1);
  const [bulkSection, setBulkSection] = useState("");
  const [bulkIds, setBulkIds] = useState([]);
  const [bulkForm, setBulkForm] = useState(EMPTY_ENTRY);
  const [bulkQuantities, setBulkQuantities] = useState({});
  const [bulkSaving, setBulkSaving] = useState(false);
  const [ricerca, setRicerca] = useState("");
  const [quickSending, setQuickSending] = useState("");
  const [diaryMessages, setDiaryMessages] = useState([]);

  useEffect(() => {
    api.get("/impostazioni").then(({data}) => {
      setSezioni((data.sezioni || []).filter(Boolean));
      setMenu(unifiedMenu(data));
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    api.get("/diario", { params: { data, ...(sezione ? { sezione } : {}) } }).then((r) => setRows(r.data)).catch(() => {});
    api.get("/diario", { params: { data } }).then((r) => { setAllRows(r.data); setFotoBimbi(r.data.map((x) => x.bimbo)); }).catch(() => {});
    api.get("/foto", { params: { data } }).then((r) => setFoto(r.data)).catch(() => {});
    if (isAdmin) api.get("/comunicazioni").then((r) => setDiaryMessages(r.data.filter((item)=>item.tipo_promemoria && item.diario_data === data))).catch(() => {});
    else setDiaryMessages([]);
  }, [data, sezione, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const openEntry = (row) => {
    setEditRow(row);
    const entry = row.entry || {};
    const legacyNap = (entry.pisolino_dalle || entry.pisolino_alle || entry.pisolino_note) ? [{ dalle: entry.pisolino_dalle || "", alle: entry.pisolino_alle || "", note: entry.pisolino_note || "" }] : [];
    setForm({ ...EMPTY_ENTRY, ...entry, pisolini: entry.pisolini?.length ? entry.pisolini : legacyNap, pranzo_primo_pietanza: entry.pranzo_primo_pietanza || entry.pranzo_pietanza || "", pranzo_primo_quantita: entry.pranzo_primo_quantita || entry.pranzo_quantita || entry.pranzo || "", merenda_quantita: entry.merenda_quantita || entry.merenda || "" });
  };

  const saveEntry = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/diario/${editRow.bimbo.id}`, { ...form, data });
      toast.success(`Diario di ${editRow.bimbo.nome} salvato`);
      setEditRow(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const sendSupplyReminder = async (child, kind) => {
    const label = kind === "pannolini" ? "pannolini" : "salviette";
    const key = `${child.id}-${kind}`;
    setQuickSending(key);
    try {
      await api.post(`/diario/${child.id}/promemoria`, { tipo: kind, data });
      toast.success(`Comunicazione “Portare ${label}” inviata alla famiglia di ${child.nome}`);
      setEditRow((current) => current?.bimbo?.id === child.id
        ? { ...current, promemoria_inviati: [...new Set([...(current.promemoria_inviati || []), kind])] }
        : current);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setQuickSending(""); }
  };

  const openBulk = () => {
    const initialSection = sezione || sezioni[0] || fotoBimbi.find((b) => b.sezione)?.sezione || "";
    const children = fotoBimbi.filter((b) => b.sezione === initialSection);
    setBulkSection(initialSection);
    setBulkIds(children.map((b) => b.id));
    setBulkForm(EMPTY_ENTRY);
    setBulkQuantities({});
    setBulkStep(1);
    setBulkOpen(true);
  };

  const changeBulkSection = (value) => {
    setBulkSection(value);
    setBulkIds(fotoBimbi.filter((b) => b.sezione === value).map((b) => b.id));
    setBulkQuantities({});
  };

  const goToBulkQuantities = () => {
    if (!bulkSection) return toast.error("Seleziona una sezione");
    if (!bulkIds.length) return toast.error("Seleziona almeno un bambino");
    setBulkQuantities(Object.fromEntries(bulkIds.map((id) => [id, Object.fromEntries(MEAL_FIELDS.map(([key]) => [key, ""]))])));
    setBulkStep(2);
  };

  const saveBulk = async () => {
    setBulkSaving(true);
    try {
      await Promise.all(bulkIds.map((id) => {
        const existing = allRows.find((row) => row.bimbo.id === id)?.entry || {};
        const quantities = bulkQuantities[id] || {};
        const sharedMeals = Object.fromEntries(MEAL_FIELDS.flatMap(([key]) => [
          [`${key}_pietanza`, bulkForm[`${key}_pietanza`] || ""],
          [`${key}_quantita`, quantities[key] || ""],
        ]));
        return api.put(`/diario/${id}`, { ...EMPTY_ENTRY, ...existing, ...sharedMeals, data });
      }));
      toast.success(`Diario salvato per ${bulkIds.length} bambini`);
      setBulkOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBulkSaving(false);
    }
  };

  const uploadFoto = async (e) => {
    e.preventDefault();
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((item) => fd.append("files", item));
      fd.append("data", data);
      fd.append("didascalia", didascalia);
      fd.append("bimbo_ids", bimboIdsSel.join(","));
      await api.post("/foto", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(files.length === 1 ? "Foto caricata" : `${files.length} foto caricate`);
      setUploadOpen(false);
      setFiles([]); setDidascalia(""); setBimboIdsSel([]); setFotoSezione("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const deleteFoto = async (f) => {
    if (!window.confirm("Eliminare questa foto?")) return;
    await api.delete(`/foto/${f.id}`);
    toast.success("Foto eliminata");
    load();
  };

  const pastoLabel = (v) => PASTO_OPTS.find(([k]) => k === v)?.[1] || "—";
  const auditDateTime = (value) => {
    if (!value) return "Data e ora non disponibili";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return new Intl.DateTimeFormat("it-IT", { dateStyle: "short", timeStyle: "short" }).format(parsed);
  };
  const bimbiFotoFiltrati = fotoSezione ? fotoBimbi.filter((b) => b.sezione === fotoSezione) : fotoBimbi;
  const idsBimbiVisibili = bimbiFotoFiltrati.map((b) => b.id);
  const tuttiVisibiliSelezionati = idsBimbiVisibili.length > 0 && idsBimbiVisibili.every((id) => bimboIdsSel.includes(id));
  const visibleSections = [...new Set((isAdmin ? [...sezioni, ...fotoBimbi.map((b) => b.sezione)] : fotoBimbi.map((b) => b.sezione)).filter(Boolean))];
  const aggiungiFile = (elenco) => setFiles((attuali) => [...attuali, ...Array.from(elenco || [])]);
  const selezionaTuttiVisibili = () => setBimboIdsSel((attuali) => tuttiVisibiliSelezionati
    ? attuali.filter((id) => !idsBimbiVisibili.includes(id))
    : [...new Set([...attuali, ...idsBimbiVisibili])]);
  const termineRicerca = ricerca.trim().toLowerCase();
  const today = new Date().toISOString().slice(0, 10);
  const pastLocked = !isAdmin && data < today;
  const rowsFiltrate = rows.filter((row) => !termineRicerca || [row.bimbo?.nome, row.bimbo?.cognome, row.bimbo?.sezione]
    .filter(Boolean).join(" ").toLowerCase().includes(termineRicerca));

  return (
    <div className="space-y-6 animate-fade-up" data-testid="diario-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl font-semibold text-[#3D405B] tracking-tight">Diario & Foto</h1>
          <p className="text-[#6C757D] mt-1 text-sm">Pisolino, pasti, bisogni e foto della giornata</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <input type="date" value={data} onChange={(e) => setData(e.target.value)} data-testid="diario-date-input" className="input-warm !w-auto" />
          <select value={sezione} onChange={(e) => setSezione(e.target.value)} data-testid="diario-sezione-select" className="input-warm !w-auto">
            <option value="">Tutte le sezioni</option>
            {visibleSections.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button disabled={pastLocked} onClick={openBulk} data-testid="bulk-diario-button" className="btn-sage" title={pastLocked ? "I giorni passati non sono modificabili" : "Compila la sezione"}>
            <NotebookPen className="w-4 h-4" /> Compila sezione
          </button>
          <button disabled={pastLocked} onClick={() => setUploadOpen(true)} data-testid="upload-foto-button" className="btn-warm" title={pastLocked ? "I giorni passati non sono modificabili" : "Carica foto"}>
            <Camera className="w-4 h-4" /> Carica foto
          </button>
        </div>
      </div>

      {pastLocked && <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800">Giornata storica in sola lettura. Soltanto l’amministratrice può modificarla.</div>}

      <div className="card-warm p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#a89a8a]" />
          <input value={ricerca} onChange={(e) => setRicerca(e.target.value)} className="input-warm !pl-10"
            placeholder="Cerca bambino per nome, cognome o sezione..." aria-label="Cerca nel diario" />
        </div>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="diario-grid">
        {rowsFiltrate.map((row) => {
          const b = row.bimbo;
          const e2 = row.entry;
          return (
            <div key={b.id} className="card-warm p-5 flex flex-col gap-3" data-testid={`diario-card-${b.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-heading font-semibold text-lg text-[#3D405B]">{b.nome} {b.cognome}</p>
                  <span className="badge-nido">{b.sezione || "Senza sezione"}</span>
                </div>
                <button disabled={pastLocked} onClick={() => openEntry(row)} data-testid={`edit-diario-${b.id}`}
                  className={`rounded-full px-4 py-1.5 text-xs font-bold transition ${e2 ? "bg-[#E8F3EE] text-[#4a7a63] hover:bg-[#d8ebe2]" : "bg-[#E07A5F] text-white hover:bg-[#D06346]"}`}>
                  {pastLocked ? "Sola lettura" : e2 ? "Modifica" : "Compila"}
                </button>
              </div>
              {e2 ? (
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="rounded-lg bg-[#FAF6F0] p-2.5 flex items-center gap-2"><Moon className="w-3.5 h-3.5 text-[#3D405B]" /><span>{e2.pisolini?.length ? `${e2.pisolini.length} pisolin${e2.pisolini.length === 1 ? "o" : "i"}` : (e2.pisolino_dalle || e2.pisolino_alle ? `${e2.pisolino_dalle || "—"} → ${e2.pisolino_alle || "—"}` : "—")}</span></div>
                  <div className="rounded-lg bg-[#FAF6F0] p-2.5 flex items-center gap-2"><Apple className="w-3.5 h-3.5 text-[#F4A261]" /><span>Spuntino: {e2.spuntino_pietanza || "—"} · {pastoLabel(e2.spuntino_quantita)}</span></div>
                  <div className="rounded-lg bg-[#FAF6F0] p-2.5 flex items-center gap-2"><Utensils className="w-3.5 h-3.5 text-[#E07A5F]" /><span>Pranzo: {[e2.pranzo_primo_pietanza || e2.pranzo_pietanza,e2.pranzo_secondo_pietanza,e2.pranzo_contorno_pietanza].filter(Boolean).join(", ") || "—"}</span></div>
                  <div className="rounded-lg bg-[#FAF6F0] p-2.5 flex items-center gap-2"><Apple className="w-3.5 h-3.5 text-[#81B29A]" /><span>Merenda: {e2.merenda_pietanza || "—"} · {pastoLabel(e2.merenda_quantita || e2.merenda)}</span></div>
                  <div className="rounded-lg bg-[#FAF6F0] p-2.5 flex items-center gap-2"><Droplets className="w-3.5 h-3.5 text-[#4a7a63]" /><span className="truncate">{e2.bisogni || "—"}</span></div>
                  {e2.note && <div className="col-span-2 rounded-lg bg-amber-50 border border-amber-200 p-2.5 flex gap-2"><NotebookPen className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" /><span className="text-amber-900">{e2.note}</span></div>}
                  {isAdmin && e2.autore && <div className="col-span-2 rounded-lg border border-[#dce8e2] bg-[#f3f8f5] px-3 py-2 text-[11px] text-[#607086]">
                    <p><span className="font-semibold text-[#3D405B]">Compilato da:</span> {e2.autore}</p>
                    <p><span className="font-semibold text-[#3D405B]">Origine:</span> {e2.origine || "App"} · {auditDateTime(e2.updated_at)}</p>
                  </div>}
                </div>
              ) : (
                <p className="text-xs text-[#a89a8a] italic">Diario non ancora compilato per oggi.</p>
              )}
              <div className="mt-auto">
                {isAdmin && diaryMessages.some((message)=>message.bimbo_id===b.id) && <div className="mt-3 space-y-2">{diaryMessages.filter((message)=>message.bimbo_id===b.id).map((message)=><div key={message.id} className="rounded-lg bg-[#FCEFEA] px-3 py-2 text-xs text-[#5b5248]"><p className="font-semibold text-[#b0563a]">{message.oggetto}</p><p className="mt-0.5">Mittente visibile al genitore: {message.autore || "APiccoliPassi"}</p><p className="text-[10px] text-[#a89a8a] mt-1">Inviata da {message.inviato_da || "Amministrazione"} · {auditDateTime(message.created_at)}</p></div>)}</div>}
              </div>
            </div>
          );
        })}
        {rowsFiltrate.length === 0 && <p className="text-[#a89a8a] col-span-full py-10 text-center" data-testid="diario-empty">{ricerca ? "Nessun bambino corrisponde alla ricerca." : "Nessun bimbo in questa sezione."}</p>}
      </div>

      <div>
        <h2 className="font-heading text-2xl font-semibold text-[#3D405B] mb-4">Foto del giorno</h2>
        {foto.length === 0 ? (
          <p className="text-sm text-[#a89a8a]" data-testid="foto-empty">Nessuna foto caricata per questa data.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="foto-grid">
            {foto.map((f) => (
              <div key={f.id} className="group relative rounded-2xl overflow-hidden border border-[#f0e4d7] bg-white" data-testid={`foto-item-${f.id}`}>
                <img src={fileUrl(f.storage_path)} alt={f.didascalia || "Foto"} onClick={() => setLightbox(f)}
                  className="w-full h-40 object-cover cursor-pointer transition duration-300 group-hover:scale-105" />
                <div className="p-2.5 flex items-center justify-between gap-2">
                  <div className="min-w-0"><p className="text-xs text-[#6C757D] truncate">{f.didascalia || "Senza didascalia"}</p>{isAdmin && f.caricata_da && <p className="text-[10px] text-[#a89a8a] truncate">Caricata da {f.caricata_da}</p>}</div>
                  {!pastLocked && <button onClick={() => deleteFoto(f)} data-testid={`delete-foto-${f.id}`} className="text-red-400 hover:text-red-600 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-4xl rounded-2xl" data-testid="bulk-diario-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#3D405B]">Compilazione di massa · {formatDate(data)}</DialogTitle>
          </DialogHeader>
          {bulkStep === 1 ? (
            <div className="space-y-5">
              <div className="rounded-xl border border-[#e8d9c8] bg-white p-4">
                <label className="label-warm block mb-2">1. Seleziona la sezione</label>
                <select value={bulkSection} onChange={(e) => changeBulkSection(e.target.value)} className="input-warm" data-testid="bulk-sezione-select">
                  <option value="">Seleziona una sezione...</option>
                  {visibleSections.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <div className="flex items-center justify-between gap-3 mt-4 mb-2">
                  <p className="label-warm">Bambini inclusi ({bulkIds.length})</p>
                  <button type="button" onClick={() => setBulkIds(bulkIds.length ? [] : fotoBimbi.filter((b) => b.sezione === bulkSection).map((b) => b.id))} className="text-xs font-semibold text-[#4a7a63]">
                    {bulkIds.length ? "Deseleziona tutti" : "Seleziona tutti"}
                  </button>
                </div>
                <div className="max-h-52 overflow-y-auto grid sm:grid-cols-2 md:grid-cols-3 gap-2">
                  {fotoBimbi.filter((b) => b.sezione === bulkSection).map((b) => <label key={b.id} className="flex items-center gap-2 rounded-lg bg-[#FAF6F0] px-3 py-2 text-sm cursor-pointer"><input type="checkbox" checked={bulkIds.includes(b.id)} onChange={(e) => setBulkIds(e.target.checked ? [...bulkIds, b.id] : bulkIds.filter((id) => id !== b.id))}/><span>{b.nome} {b.cognome}</span></label>)}
                </div>
              </div>
              <div className="space-y-3">
                <p className="label-warm">2. Menù unico</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {MEAL_FIELDS.map(([key,label,Icon]) => <div key={key} className="rounded-xl bg-[#FAF6F0] p-4"><label className="label-warm mb-2 flex items-center gap-2"><Icon className="w-4 h-4"/> {label}</label><select value={bulkForm[`${key}_pietanza`]} onChange={(e) => setBulkForm({...bulkForm,[`${key}_pietanza`]:e.target.value})} className="input-warm"><option value="">Non previsto</option>{(menu[key] || []).map((item) => <option key={item} value={item}>{item}</option>)}</select></div>)}
                </div>
              </div>
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3">
                <button type="button" onClick={() => setBulkOpen(false)} className="rounded-full px-5 py-2.5 text-sm font-semibold text-[#6C757D] hover:bg-[#f0e4d7]">Annulla</button>
                <button type="button" onClick={goToBulkQuantities} className="btn-warm" data-testid="bulk-next-button">Avanti · Quanto hanno mangiato</button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div><p className="label-warm">3. Quantità per ogni bambino</p><p className="text-sm text-[#6C757D] mt-1">Imposta individualmente quanto ha mangiato ciascun bambino.</p></div>
              <div className="space-y-3">
                {fotoBimbi.filter((b) => bulkIds.includes(b.id)).map((b) => <div key={b.id} className="rounded-xl border border-[#e8d9c8] bg-white p-4"><p className="font-heading font-semibold text-[#3D405B] mb-3">{b.nome} {b.cognome}</p><div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{MEAL_FIELDS.filter(([key]) => bulkForm[`${key}_pietanza`]).map(([key,label]) => <div key={key}><label className="text-xs font-semibold text-[#6C757D] block mb-1">{label} · {bulkForm[`${key}_pietanza`]}</label><select value={bulkQuantities[b.id]?.[key] || ""} onChange={(e) => setBulkQuantities({...bulkQuantities,[b.id]:{...(bulkQuantities[b.id] || {}),[key]:e.target.value}})} className="input-warm"><option value="">Quanto ha mangiato...</option>{PASTO_OPTS.filter(([v]) => v !== "meta").map(([v,l]) => <option key={v} value={v}>{l}</option>)}</select></div>)}</div></div>)}
              </div>
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3">
                <button type="button" onClick={() => setBulkStep(1)} className="rounded-full px-5 py-2.5 text-sm font-semibold text-[#6C757D] hover:bg-[#f0e4d7]">Indietro</button>
                <button type="button" disabled={bulkSaving} onClick={saveBulk} className="btn-warm" data-testid="bulk-save-button">{bulkSaving ? "Salvataggio..." : `Salva ${bulkIds.length} diari`}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editRow} onOpenChange={() => setEditRow(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="diario-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#3D405B]">
              Diario di {editRow?.bimbo.nome} · {formatDate(data)}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={saveEntry} className="space-y-4 mt-2" data-testid="diario-form">
            <div className="rounded-xl bg-[#FAF6F0] p-4 space-y-3">
              <div className="flex items-center justify-between gap-3"><p className="label-warm flex items-center gap-2"><Moon className="w-4 h-4" /> Pisolini</p><button type="button" onClick={()=>{const ora=currentTime24();setForm({...form,pisolini:[...(form.pisolini||[]),{dalle:ora,alle:"",note:""}]});}} className="btn-sage !py-1.5 !px-3 text-xs"><Plus className="w-3.5 h-3.5"/> Aggiungi</button></div>
              {(form.pisolini||[]).length === 0 && <p className="text-xs text-[#a89a8a]">Nessun pisolino registrato.</p>}
              {(form.pisolini||[]).map((nap,index)=><div key={index} className="rounded-xl border border-[#eadfd2] bg-white p-3 space-y-2"><div className="flex justify-between items-center"><span className="text-xs font-semibold text-[#6C757D]">Pisolino {index+1}</span><button type="button" onClick={()=>setForm({...form,pisolini:form.pisolini.filter((_,i)=>i!==index)})} className="text-red-500"><Trash2 className="w-4 h-4"/></button></div><div className="grid grid-cols-2 gap-3"><div><label className="text-xs text-[#6C757D] block mb-1">Dalle</label><NumericTimeInput label={`Inizio pisolino ${index+1}`} value={nap.dalle} onChange={value=>setForm({...form,pisolini:form.pisolini.map((x,i)=>i===index?{...x,dalle:value}:x)})}/></div><div><label className="text-xs text-[#6C757D] block mb-1">Alle</label><NumericTimeInput label={`Fine pisolino ${index+1}`} value={nap.alle} afterValue={nap.dalle} onChange={value=>setForm({...form,pisolini:form.pisolini.map((x,i)=>i===index?{...x,alle:value}:x)})}/></div></div><input value={nap.note||""} onChange={e=>setForm({...form,pisolini:form.pisolini.map((x,i)=>i===index?{...x,note:e.target.value}:x)})} className="input-warm" placeholder="Note sul sonno (opzionale)"/></div>)}
            </div>
            <div className="space-y-3">
              <p className="text-xs font-semibold text-[#6C757D]">Menù unico</p>
              {MEAL_FIELDS.map(([key,label,Icon])=><div key={key} className="rounded-xl bg-[#FAF6F0] p-4"><label className="label-warm mb-2 flex items-center gap-2"><Icon className="w-4 h-4"/> {label}</label><div className="grid sm:grid-cols-2 gap-3"><select value={form[`${key}_pietanza`]} onChange={e=>setForm({...form,[`${key}_pietanza`]:e.target.value})} className="input-warm"><option value="">Seleziona dal menù...</option>{(menu[key]||[]).map(item=><option key={item} value={item}>{item}</option>)}</select><select value={form[`${key}_quantita`]} onChange={e=>setForm({...form,[`${key}_quantita`]:e.target.value})} className="input-warm"><option value="">Quanto ha mangiato...</option>{PASTO_OPTS.filter(([v])=>v!=="meta").map(([v,l])=><option key={v} value={v}>{l}</option>)}{form[`${key}_quantita`] === "meta" && <option value="meta">Metà (storico)</option>}</select></div></div>)}
            </div>
            <div>
              <label className="label-warm block mb-1.5 flex items-center gap-2"><Droplets className="w-4 h-4" /> Bisogni</label>
              <input value={form.bisogni} onChange={(e) => setForm({ ...form, bisogni: e.target.value })} data-testid="bisogni-input" className="input-warm" placeholder="Es. 1 scarica, pipì regolare" />
            </div>
            <div className="rounded-xl border border-[#eadfd2] bg-[#FAF6F0] p-3 sm:p-4">
              <p className="label-warm mb-2">Comunicazioni rapide alla famiglia</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button type="button" disabled={pastLocked||Boolean(quickSending)||(editRow?.promemoria_inviati||[]).includes("pannolini")} onClick={()=>sendSupplyReminder(editRow.bimbo,"pannolini")} className={`rounded-xl border px-2.5 py-2.5 text-xs font-semibold flex items-center justify-center gap-1.5 disabled:cursor-not-allowed ${(editRow?.promemoria_inviati||[]).includes("pannolini")?"border-emerald-200 bg-emerald-50 text-emerald-700":"border-[#e8d9c8] bg-white text-[#3D405B] hover:border-[#E07A5F] disabled:opacity-50"}`}><Package className="w-4 h-4 text-[#E07A5F]"/>{(editRow?.promemoria_inviati||[]).includes("pannolini")?"Pannolini · Inviato":quickSending===`${editRow?.bimbo.id}-pannolini`?"Invio...":"Portare pannolini"}</button>
                <button type="button" disabled={pastLocked||Boolean(quickSending)||(editRow?.promemoria_inviati||[]).includes("salviette")} onClick={()=>sendSupplyReminder(editRow.bimbo,"salviette")} className={`rounded-xl border px-2.5 py-2.5 text-xs font-semibold flex items-center justify-center gap-1.5 disabled:cursor-not-allowed ${(editRow?.promemoria_inviati||[]).includes("salviette")?"border-emerald-200 bg-emerald-50 text-emerald-700":"border-[#e8d9c8] bg-white text-[#3D405B] hover:border-[#81B29A] disabled:opacity-50"}`}><Droplets className="w-4 h-4 text-[#4a7a63]"/>{(editRow?.promemoria_inviati||[]).includes("salviette")?"Salviette · Inviato":quickSending===`${editRow?.bimbo.id}-salviette`?"Invio...":"Portare salviette"}</button>
              </div>
            </div>
            <div>
              <label className="label-warm block mb-1.5 flex items-center gap-2"><NotebookPen className="w-4 h-4" /> Note per i genitori</label>
              <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="note-input" className="input-warm" rows={3} placeholder="Umore, attività, cose da sapere..." />
            </div>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setEditRow(null)} data-testid="diario-cancel-button" className="rounded-full px-5 py-2.5 text-sm font-semibold text-[#6C757D] hover:bg-[#f0e4d7] transition">Annulla</button>
              <button type="submit" data-testid="diario-save-button" className="btn-warm">Salva diario</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="upload-foto-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#3D405B]">Carica foto · {formatDate(data)}</DialogTitle>
          </DialogHeader>
          <form onSubmit={uploadFoto} className="space-y-4 mt-2" data-testid="upload-foto-form">
            <div className="grid grid-cols-2 gap-3">
              <label className="btn-warm cursor-pointer justify-center"><Camera className="w-4 h-4" /> Scatta foto<input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { aggiungiFile(e.target.files); e.target.value = ""; }} data-testid="foto-camera-input" /></label>
              <label className="btn-sage cursor-pointer justify-center"><Plus className="w-4 h-4" /> Scegli dalla galleria<input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { aggiungiFile(e.target.files); e.target.value = ""; }} data-testid="foto-file-input" /></label>
            </div>
            {files.length > 0 && <div className="rounded-xl bg-[#FAF6F0] p-3 space-y-2"><p className="text-sm font-semibold text-[#3D405B]">{files.length} {files.length === 1 ? "foto selezionata" : "foto selezionate"}</p>{files.map((item, index) => <div key={`${item.name}-${index}`} className="flex items-center justify-between gap-2 text-xs text-[#6C757D]"><span className="truncate">{item.name}</span><button type="button" onClick={() => setFiles(files.filter((_, i) => i !== index))} className="text-red-500" aria-label={`Rimuovi ${item.name}`}><Trash2 className="w-4 h-4" /></button></div>)}</div>}
            <input value={didascalia} onChange={(e) => setDidascalia(e.target.value)} data-testid="foto-didascalia-input" className="input-warm" placeholder="Didascalia (opzionale)" />
            <div className="rounded-xl border border-[#e8d9c8] bg-white p-3"><div className="flex items-center justify-between gap-3 mb-2"><p className="label-warm">Bambini nella foto</p><button type="button" onClick={selezionaTuttiVisibili} className="text-xs font-semibold text-[#4a7a63]">{tuttiVisibiliSelezionati ? "Deseleziona visibili" : "Seleziona visibili"}</button></div><select value={fotoSezione} onChange={(e) => setFotoSezione(e.target.value)} className="input-warm mb-2" data-testid="foto-sezione-select"><option value="">Tutte le sezioni</option>{visibleSections.map((s) => <option key={s} value={s}>{s}</option>)}</select><p className="text-xs text-[#a89a8a] mb-3">{bimboIdsSel.length ? `${bimboIdsSel.length} bambini selezionati.` : "Se non selezioni nessuno, le foto saranno visibili a tutte le famiglie."}</p><div className="max-h-48 overflow-y-auto grid sm:grid-cols-2 gap-2">{bimbiFotoFiltrati.map((b) => <label key={b.id} className="flex items-center gap-2 rounded-lg bg-[#FAF6F0] px-3 py-2 text-sm cursor-pointer"><input type="checkbox" checked={bimboIdsSel.includes(b.id)} onChange={(e) => setBimboIdsSel(e.target.checked ? [...new Set([...bimboIdsSel, b.id])] : bimboIdsSel.filter((id) => id !== b.id))}/><span>{b.nome} {b.cognome}</span></label>)}</div></div>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setUploadOpen(false)} data-testid="foto-cancel-button" className="rounded-full px-5 py-2.5 text-sm font-semibold text-[#6C757D] hover:bg-[#f0e4d7] transition">Annulla</button>
              <button type="submit" disabled={uploading || !files.length} data-testid="foto-upload-submit" className="btn-warm">{uploading ? "Caricamento..." : `Carica${files.length > 1 ? ` (${files.length})` : ""}`}</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="foto-lightbox">
          <button className="absolute top-4 right-4 text-white"><X className="w-8 h-8" /></button>
          <img src={fileUrl(lightbox.storage_path)} alt="" className="max-h-[85vh] max-w-full rounded-2xl shadow-2xl" />
        </div>
      )}
    </div>
  );
}
